/// <amd-module name="Test/TestResultsFilter/TestResultsFilter" />
import { SelectionMode } from "OfficeFabric/Selection";
import * as React from "react";
import { ViewContextStatus } from "Test/common-util/Common";
import { first } from "Test/common-util/Common.Utils";
import { FilterState, FilterValue, IFilter, IFilterOptions } from "Test/common-util/Contracts/Filters";
import { FilterActionsCreatorBase } from "Test/TestResultsFilter/Actions/FilterActionsCreatorBase";
import { DataSourceFilterValueProvider } from "Test/TestResultsFilter/DataSourceFilterValueProvider";
import * as Resources from "Test/TestResultsFilter/Resources";
import { FilterStoreBase } from "Test/TestResultsFilter/Stores/FilterStoreBase";
import {
    ITestResultsFilterField,
    ITestResultsFilterItem,
    ITestResultsFilterItemProvider,
    ITestResultsFilterPickListItem,
    TestResultsFilterFieldType
} from "Test/TestResultsFilter/Types";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { equals, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { PickListFilterBarItem } from "VSSUI/Components/PickList/PickListFilterBarItem";
import { FilterBar, IFilterBar } from "VSSUI/FilterBar";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import {
    Filter,
    FILTER_CHANGE_EVENT,
    FilterOperatorType,
    IFilter as IVSSUIFilter,
    IFilterItemState,
    IFilterState as IVSSUIFilterState
} from "VSSUI/Utilities/Filter";

/**
 * Properties required for the TestResultsFilter
 */
export interface ITestResultsFilterProps {
    fields: ITestResultsFilterField[];

    /** Optional VSS filter object to subscribe to, if not passed (or explicitly set as 'undefined') a new one will be created */
    filter?: IVSSUIFilter;

    /** Optional initial filter state to apply to the filter */
    initialFilterState?: FilterState;

    /** Optional value indicating whether the component should set a default filter state based on the passed in fields. Defaults to true */
    setDefaultFilter?: boolean;

    /** Optional callback once the control has been rendered */
    onRenderComplete?: () => void;

    store: FilterStoreBase;

    actionCreator: FilterActionsCreatorBase;

    /** Optional callback to react to filter changes
     * @param filterState Current state of the filter (not only changes)
     */
    filterUpdatedCallback?(filterState: FilterState): void;
}

/**
 * Current filter bar state.
 */
export interface ITestResultsFilterState {
    filter: IVSSUIFilter;
    fieldValues: { [key: string]: FilterValue[] };
}

/**
 * React based class for filtering test result artifacts.
 */
export class TestResultsFilter extends React.Component<ITestResultsFilterProps, ITestResultsFilterState> {
    public static defaultProps = {
        setDefaultFilter: true
    };

    private _suppressFilterUpdatedEvent: boolean;
    private _updateThrottleDelegate: Function;
    private _filterBar: IFilterBar;
    private _timerManagement: TimerManagement;

    constructor(props: ITestResultsFilterProps, context?: any) {
        super(props, context);
        this._timerManagement = new TimerManagement(this);
        this._updateThrottleDelegate = this._timerManagement.throttle(() => {}, 250);

        // Use passed in filter, or start with a new, empty filter
        const { filter = new Filter() } = this.props;
        this.state = { filter: filter, fieldValues: this.props.store.getState() };
    }

    public componentDidMount() {
        const { filter } = this.state;
        const { fields, initialFilterState, setDefaultFilter } = this.props;

        // Set default state for the filter
        if (setDefaultFilter) {
            filter.setDefaultState(generateDefaultFilterState(fields));
            filter.reset();
        }

        // Apply the initial state of the filter, if specified
        if (initialFilterState) {
            resolveFilterState(fields, initialFilterState).then(
                resolvedFilterState => {
                    this.state.filter.setState(resolvedFilterState);
                    this._subscribeToFilterChangeEvent(filter);
                },
                error => {
                    console.log(JSON.stringify(error));
                }
            );
        } else {
            this._subscribeToFilterChangeEvent(filter);
        }

        if (this.props.onRenderComplete) {
            this.props.onRenderComplete();
        }

        this.props.store.addChangedListener(this._handleStoreChange);
    }

    public componentWillUnmount() {
        const { filter } = this.state;
        if (filter) {
            this._unsubscribeFromFilterChangeEvent(filter);
        }
        this.props.store.removeChangedListener(this._handleStoreChange);
    }

    /**
     * Clears all filters in the UI.
     */
    public clearFilters() {
        this._suppressFilterUpdatedEvent = true;
        this.state.filter.reset();
        this._suppressFilterUpdatedEvent = false;
    }

    /**
     * Set focus to the filter bar.
     */
    public focus() {
        if (this._filterBar) {
            this._filterBar.focus();
        }
    }

    /**
     * Handles when we should refresh our view.  Asynchronously clears out the cached set of picklist values and causes a render.
     * This is generally called when data changes in the UI, like when the user is editing a test result on the grid, since
     * this can cause the set of allowed values showing up in the filter to change.
     */
    public update() {
        // Update can be called repeatedly on test result change events (like typing in the test result title) so
        // we want to delay re-rendering the control a bit.
        this._updateThrottleDelegate();
    }

    public render(): JSX.Element {
        const { filter } = this.state;
        const { fields } = this.props;

        const filters: JSX.Element[] = fields.map(filterField => {
            if (filterField.displayType === TestResultsFilterFieldType.Text) {
                return (
                    <KeywordFilterBarItem
                        key={filterField.fieldName}
                        filter={filter}
                        filterItemKey={filterField.fieldName}
                        placeholder={filterField.placeholder}
                    />
                );
            } else {
                return (
                    <PickListFilterBarItem
                        className="test-results-filter-bar-item"
                        key={filterField.fieldName}
                        filter={filter}
                        filterItemKey={filterField.fieldName}
                        selectionMode={SelectionMode.multiple}
                        getPickListItems={() => this._getItemsToRender(filterField.fieldName)}
                        getListItem={(item: ITestResultsFilterItem) => this._getPickListItem(filterField.fieldName, item)}
                        placeholder={filterField.placeholder}
                        noItemsText={filterField.noItemsText}
                        showOrAndOperators={filterField.showOrAndOperators}
                        showSelectAll={false}
                        isSearchable={true}
                        searchTextPlaceholder={filterField.searchTextPlaceholder || Resources.FilterSearchPlaceholderText}
                        disabled={filterField.disabled}
                    />
                );
            }
        });

        return (
            <FilterBar
                filter={filter}
                className="test-results-filter-bar"
                onRenderComplete={this.props.onRenderComplete}
                componentRef={this._resolveFilterBar}
            >
                {filters}
            </FilterBar>
        );
    }

    /**
     * Retrieves the initial set of values to show.  These are just text, during render for the picklist 'getPickListItem' is called
     * which will return the actual data to be rendered.
     * @param fieldName Name of the field to get the unique values for
     */
    protected _getItemsToRender(fieldName: string): ITestResultsFilterItem[] {
        let itemsToRender: ITestResultsFilterItem[] | undefined = undefined;

        const isFieldPresent = this.props.store.getState();

        if (!isFieldPresent || !isFieldPresent[fieldName]) {
            // Add temporary "Loading" item, data get populated in the store
            itemsToRender = [
                {
                    key: `_${Resources.Loading}`,
                    display: Resources.Loading,
                    value: "",
                    disabled: true
                }
            ];

            this.props.actionCreator.fetchFilterValues(fieldName);
        } else {
            const fieldValueProvider = this._getFieldValueProvider(fieldName);
            itemsToRender = fieldValueProvider.getItemsForValues(this.state.fieldValues[fieldName]);
        }

        return itemsToRender;
    }

    /**
     * Called when rendering an item in a picklist.  Depending on the field different icons (etc) are rendered.
     * @param fieldName Name of the field that the value is associated with
     * @param value Value to generate a picklist entry for.
     */
    protected _getPickListItem(fieldName: string, value: ITestResultsFilterItem): ITestResultsFilterPickListItem {
        const valueProvider = this._getFieldValueProvider(fieldName);
        const result = valueProvider.getListItem(value);

        return result;
    }
    private _resolveFilterBar = (filterBar: IFilterBar) => {
        this._filterBar = filterBar;
    };

    private _subscribeToFilterChangeEvent(filter: IVSSUIFilter) {
        filter.subscribe(this._handleFilterUpdated, FILTER_CHANGE_EVENT);
    }

    private _unsubscribeFromFilterChangeEvent(filter: IVSSUIFilter) {
        filter.unsubscribe(this._handleFilterUpdated, FILTER_CHANGE_EVENT);
    }

    private _handleStoreChange = () => {
        this.setState({ filter: this.state.filter, fieldValues: this.props.store.getState() });
    };

    private _getFieldValueProvider(fieldName: string): ITestResultsFilterItemProvider {
        const field = this._getField(fieldName);

        if (field && field.valueProvider) {
            return field.valueProvider;
        }

        return new DataSourceFilterValueProvider(fieldName);
    }

    private _getField(fieldName: string): ITestResultsFilterField | undefined {
        const { fields } = this.props;
        return first(fields, f => equals(f.fieldName, fieldName, true));
    }

    private _handleFilterUpdated = (changedState: IVSSUIFilterState): void => {
        if (this.props.filterUpdatedCallback && !this._suppressFilterUpdatedEvent) {
            // changedState is only the last change, we need the full state
            const { filter } = this.state;
            if (filter) {
                const filterState = mapToFilterState(filter.getState());
                this.props.filterUpdatedCallback(filterState);
            }
        }
    };
}

/**
 * Convert a VSSF filter state to the FilterManager's state
 * @param filter VSSF filter instance
 */
export function mapToFilterState(filterState: IVSSUIFilterState): FilterState {
    const filters: { [key: string]: IFilter } = {};

    for (const filterKey in filterState) {
        const currentFilter = filterState[filterKey];
        let value: ITestResultsFilterItem | ITestResultsFilterItem[] | string = currentFilter && currentFilter.value;
        if (!!value) {
            if (typeof value === "string") {
                value = [
                    {
                        value
                    } as ITestResultsFilterItem
                ];
            } else if (!(value instanceof Array)) {
                value = [value];
            }

            const mappedFilter = {
                values: value.map(v => v.value)
            } as IFilter;

            if (currentFilter && currentFilter.operator === FilterOperatorType.and) {
                mappedFilter.options = {
                    useAndOperator: true
                };
            }

            if (mappedFilter.values && mappedFilter.values.length) {
                filters[filterKey] = mappedFilter;
            }
        }
    }

    return filters;
}

/**
 * Convert FilterManager's state to the VSSF filter state
 * @param filter VSSF filter instance
 */
export function mapFromFilterState(filterState: FilterState): IVSSUIFilterState {
    const vssFilterState: IVSSUIFilterState = {};

    for (const filterKey in filterState) {
        const currentFilter = filterState[filterKey];
        if (currentFilter.values && currentFilter.values.length) {
            const filter: IFilterItemState = {
                value: currentFilter.values.map(
                    v =>
                        ({
                            key: v,
                            value: v
                        } as ITestResultsFilterItem)
                )
            };

            if (currentFilter.options && currentFilter.options.useAndOperator) {
                filter.operator = FilterOperatorType.and;
            } else {
                filter.operator = FilterOperatorType.or;
            }

            vssFilterState[filterKey] = filter;
        }
    }

    return vssFilterState;
}

/**
 * Generate default filter state including operators for the given list of fields
 * @param fields Fields to generate default state of
 */
export function generateDefaultFilterState(fields: ITestResultsFilterField[]): IVSSUIFilterState {
    const filterState: IVSSUIFilterState = {};

    for (const field of fields) {
        if (field.showOrAndOperators) {
            filterState[field.fieldName] = {
                value: undefined,
                operator: FilterOperatorType.or
            };
        }
    }

    return filterState;
}

/**
 * Resolve a given filterstate to a VSSUI filter state that can be used to interact with the VSSUI filter
 * @param fields Filter fields that provide filter value providers
 * @param filterState Filter state to resolve
 */
export function resolveFilterState(fields: ITestResultsFilterField[], filterState: FilterState): Promise<IVSSUIFilterState> {
    const promises: Promise<{
        field: ITestResultsFilterField;
        values: ITestResultsFilterItem[] | string;
        options: IFilterOptions | undefined;
    }>[] = [];

    for (const fieldName in filterState) {
        const filter = filterState[fieldName];
        const field = first(fields, f => f.fieldName === fieldName);

        if (field) {
            if (field.displayType === TestResultsFilterFieldType.Text) {
                if (filter.values && filter.values.length === 1) {
                    promises.push(
                        Promise.resolve({
                            field,
                            values: String(first(filter.values)),
                            options: filter.options
                        })
                    );
                }
            } else {
                const valueProvider = field.valueProvider;
                if (valueProvider) {
                    const result = valueProvider.getItemsForValues(filter.values);
                    promises.push(
                        Promise.resolve(result).then(values => ({
                            field,
                            values,
                            options: filter.options
                        }))
                    );
                } else {
                    // Use default value provider
                    const fieldValueProvider = new DataSourceFilterValueProvider(fieldName);
                    promises.push(
                        Promise.resolve(fieldValueProvider.getItemsForValues(filter.values)).then(values => ({
                            field,
                            values,
                            options: filter.options
                        }))
                    );
                }
            }
        }
    }

    return Promise.all(promises).then(results => {
        // Start with the default state so that we get the correct operators and then extend it
        const resolvedFilterState: IVSSUIFilterState = generateDefaultFilterState(fields);

        for (const { field, values, options } of results) {
            let operator: FilterOperatorType | undefined = undefined;

            if (field.showOrAndOperators) {
                operator = options && options.useAndOperator ? FilterOperatorType.and : FilterOperatorType.or;
            }

            resolvedFilterState[field.fieldName] = {
                value: values,
                operator: operator
            };
        }

        return resolvedFilterState;
    });
}

export const filterValueComparer: { (a: FilterValue, b: FilterValue): number } = (a: FilterValue, b: FilterValue): number => {
    if (typeof a === "string" && typeof b === "string") {
        return localeIgnoreCaseComparer(a, b);
    } else {
        if (a == b) {
            return 0;
        } else if (a > b) {
            return 1;
        } else {
            return -1;
        }
    }
};
